// app/components/news/NewsDetail.tsx
"use client";
import React, { useEffect, useState } from "react";
import { useParams } from "next/navigation";
import {
  Container,
  Typography,
  Box,
  Grid,
  Breadcrumbs,
  Divider,
  Modal,
  IconButton,
  Fade,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Link from "next/link";
import { useTranslations } from "next-intl";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import Image from "next/image";
import { renderWithLineBreaks } from "@/app/utils/renderWithLineBreaks";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

interface NewsItem {
  id: string | number;
  title_zh: string;
  title_en: string;
  subtitle_zh: string;
  subtitle_en: string;
  img1: string;
  img2: string;
  [key: string]: any; // Allow for additional properties like img3, img4, etc.
  date: string;
  category_zh: string;
  category_en: string;
  descriptionZh: string;
  descriptionEn: string;
  slug_zh: string;
  slug_en: string;

  // ✅ fields ใหม่สำหรับ image gallery ตาม device
  coverDesktopUrl?: string;
  coverTabletUrl?: string;
  coverMobileUrl?: string;
  galleryDesktopUrls?: string[];
  galleryTabletUrls?: string[];
  galleryMobileUrls?: string[];
}

function getBackgroundImage({ src }: { src: string }) {
  return {
    backgroundImage: `url(${src})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
  };
}

// ✅ helper: สร้าง gallery ต่อ device + เติม coverX ถ้ารูปน้อยกว่า 4
function buildGallery(raw: unknown, coverUrl?: string | null): string[] {
  const base: string[] = Array.isArray(raw)
    ? (raw as unknown[])
        .map((v) => (typeof v === "string" ? v : ""))
        .filter(Boolean)
    : [];

  const unique = Array.from(new Set(base));
  if (coverUrl && unique.length < 4 && !unique.includes(coverUrl)) {
    unique.push(coverUrl);
  }
  return unique.slice(0, 4);
}

export default function NewsDetail({ initialTitle }: { initialTitle?: string }) {
  const t = useTranslations("News");
  const [currentUrl, setCurrentUrl] = useState("");

  const headerFixedRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleScroll = () => {
      if (!headerFixedRef.current) return;
      const scrollTop = headerFixedRef.current.getBoundingClientRect().top;
      if (scrollTop > 0) {
        headerFixedRef.current.classList.add("fixed");
      } else {
        //headerFixedRef.current.classList.remove("fixed");
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // เก็บ current URL ไว้ใช้แชร์
  useEffect(() => {
    if (typeof window !== "undefined") {
      setCurrentUrl(window.location.href);
    }
  }, []);

  // รับ slug และ locale จาก useParams (เหมือนเดิม)
  const params = useParams();
  const slugParam = params?.slug as string | undefined;
  const locale = (params?.locale as string) || "en";

  const [newsItem, setNewsItem] = useState<NewsItem | null>(null);
  const [relatedNews, setRelatedNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // ❗ state ของ lightbox ต้องอยู่ที่นี่ (ก่อน return เงื่อนไขใด ๆ)
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  // เพิ่ม useTheme และ useMediaQuery เพื่อตรวจสอบ viewport
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  useEffect(() => {
    if (!slugParam) return;

    const fetchNews = async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/news?locale=${locale}`, {
          cache: "no-store",
        });

        if (!res.ok) {
          console.error("[NewsDetail] fetch failed:", res.status);
          setNewsItem(null);
          setRelatedNews([]);
          return;
        }

        const data = await res.json();
        const rows: any[] = Array.isArray(data)
          ? data
          : Array.isArray(data?.items)
          ? data.items
          : [];

        // map ให้มี img1/img2 ตามดีไซน์เดิม
        const normalized: NewsItem[] = rows.map((row) => ({
          ...row,
          img1: row.img1 ?? row.coverImageUrl ?? "",
          img2: row.img2 ?? row.img2 ?? "",
        }));

        // 🔍 หา newsItem จาก slug ตามภาษา พร้อม fallback ข้ามภาษา
        const target = normalized.find((item) =>
          locale === "zh"
            ? item.slug_zh === slugParam || item.slug_en === slugParam
            : item.slug_en === slugParam || item.slug_zh === slugParam
        );

        if (!target) {
          setNewsItem(null);
          setRelatedNews([]);
          return;
        }

        setNewsItem(target);

        const isEnglishLocal = locale === "en";

        const related = normalized
          .filter(
            (item) =>
              (isEnglishLocal
                ? item.category_en === target.category_en
                : item.category_zh === target.category_zh) &&
              item.id !== target.id
          )
          .sort(() => 0.5 - Math.random())
          .slice(0, 3);

        setRelatedNews(related);
      } catch (err) {
        console.error("[NewsDetail] fetch error:", err);
        setNewsItem(null);
        setRelatedNews([]);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, [slugParam, locale]);

  // ระหว่างโหลด: ถ้ามี initialTitle ให้ render H1 ทันทีเพื่อให้ crawler เห็น
  if (loading) {
    if (!initialTitle) return null;
    return (
      <Box id="news-detail" sx={{ minHeight: "60vh" }}>
        <Container sx={{ pb: { xs: 3, md: 5 } }}>
          <Typography variant="h1" sx={{ my: { xs: 3, md: 4 } }}>
            {initialTitle}
          </Typography>
        </Container>
      </Box>
    );
  }

  // หากไม่พบข่าว
  if (!newsItem) {
    return (
      <Container sx={{ py: 5 }}>
        <Typography variant="h4" sx={{
          textAlign: "center"
        }}>
          ไม่พบข่าวนี้
        </Typography>
      </Container>
    );
  }

  const isEnglish = locale === "en";
  const description = isEnglish
    ? newsItem.descriptionEn
    : newsItem.descriptionZh;

  const relatedNewsLocal = relatedNews;

  const buildDetailUrl = (slug: string) => {
    const encoded = encodeURIComponent(slug);
    return locale === "zh"
      ? `/zh/news/detail/${encoded}`
      : `/news/detail/${encoded}`;
  };

  // คำนวณรูปต่าง ๆ หลังจากมั่นใจว่า newsItem ไม่ null แล้ว
  const otherImages = Object.keys(newsItem)
    .filter(
      (key) =>
        key.startsWith("img") &&
        key !== "img1" &&
        typeof newsItem[key] === "string" &&
        newsItem[key]
    )
    .map((key) => newsItem[key] as string);

  // ✅ base images เดิม (hero + img2,img3,...)
  const baseImages = [newsItem.img1, ...otherImages];

  // ✅ build gallery ตาม device + เติม cover ถ้า < 4
  const galleryDesktop = buildGallery(
    newsItem.galleryDesktopUrls,
    newsItem.coverDesktopUrl
  );
  const galleryTablet = buildGallery(
    newsItem.galleryTabletUrls,
    newsItem.coverTabletUrl
  );
  const galleryMobile = buildGallery(
    newsItem.galleryMobileUrls,
    newsItem.coverMobileUrl
  );

  // ใช้สำหรับ lightbox: ใช้เฉพาะ gallery ของ viewport ปัจจุบัน + baseImages
  let currentGallery: string[] = [];
  let galleryStartIndex = baseImages.length;
  
  if (isDesktop) {
    currentGallery = galleryDesktop;
  } else if (isTablet) {
    currentGallery = galleryTablet;
  } else if (isMobile) {
    currentGallery = galleryMobile;
  }

  const allImages = [...baseImages, ...currentGallery];

  const handleOpenLightbox = (index: number) => {
    setSelectedImageIndex(index);
    setLightboxOpen(true);
  };

  const handleCloseLightbox = () => setLightboxOpen(false);

  const handlePrevImage = () => {
    setSelectedImageIndex((prevIndex) =>
      prevIndex === 0 ? allImages.length - 1 : prevIndex - 1
    );
  };
  const handleNextImage = () => {
    setSelectedImageIndex((prevIndex) =>
      prevIndex === allImages.length - 1 ? 0 : prevIndex + 1
    );
  };

  const shareTitle = isEnglish ? newsItem.title_en : newsItem.title_zh;
  const encodedUrl = encodeURIComponent(currentUrl);
  const encodedTitle = encodeURIComponent(shareTitle);

  // เตรียมลิงก์สำหรับแชร์บนโซเชียล (layout เดิม)
  const socialShares = [
    {
      name: "Facebook",
      icon: "/assets/social-facebook.png",
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    },
    { name: "Instagram", icon: "/assets/social-instagram.png", url: "#" }, // Placeholder
    {
      name: "X",
      icon: "/assets/social-x.png",
      url: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
    },
    {
      name: "Pinterest",
      icon: "/assets/social-pinterest.png",
      url: `https://pinterest.com/pin/create/button/?url=${encodedUrl}&media=${encodeURIComponent(
        (process.env.NEXT_PUBLIC_BASE_URL || "") + newsItem.img1
      )}&description=${encodedTitle}`,
    },
    { name: "TikTok", icon: "/assets/social-tiktok.png", url: "#" }, // Placeholder
    { name: "WeChat", icon: "/assets/social-wechat.png", url: "#" }, // Placeholder
  ];

  // เลือก cover image ตาม viewport
  const coverImage = isDesktop 
    ? (newsItem.coverDesktopUrl || newsItem.img1)
    : isTablet
    ? (newsItem.coverTabletUrl || newsItem.img1)
    : (newsItem.coverMobileUrl || newsItem.img1);

  return (
    <Box id="news-detail">
      {/* BreadCrumbs */}
      <Box className="news-breadcrumbs">
        <Container>
          <Breadcrumbs
            aria-label="breadcrumb"
            separator={<NavigateNextIcon fontSize="small" />}
          >
            <Link href={locale === "zh" ? "/zh/news" : "/news"}>
              {t("News")}
            </Link>
            <Typography variant="body2">
              {isEnglish ? newsItem.title_en : newsItem.title_zh}
            </Typography>
          </Breadcrumbs>
        </Container>
      </Box>
      <Container data-aos="fade-up" sx={{ pb: { xs: 3, md: 5 } }}>
        <Grid container spacing={4}>
          {/* Content container ที่ปรับขนาดตาม viewport */}
          <Grid 
            size={{ 
              xs: 12,     // 12/12 (0 offset) บน mobile
              sm: 12,     // 12/12 (0 offset) บน small
              md: 10,     // 10/12 (1 offset ซ้าย-ขวา) บน tablet
              lg: 8       // 8/12 (2 offset ซ้าย-ขวา) บน desktop
            }}
            sx={{ 
              mx: 'auto'  // จัดกึ่งกลางตามขนาดที่กำหนด
            }}
          >
            <Grid size={12}>
              <Box
                sx={{ cursor: "pointer" }}
                onClick={() => handleOpenLightbox(0)}
              >
                <Image
                  src={coverImage}
                  width={1200}
                  height={650}
                  priority
                  title={isEnglish ? newsItem.title_en : newsItem.title_zh}
                  alt={isEnglish ? newsItem.title_en : newsItem.title_zh}
                  className="img-fluid"
                />
              </Box>

              <Typography variant="h1" sx={{
                my: { xs: 3, md: 4 }
              }}>
                {renderWithLineBreaks(
                  isEnglish ? newsItem.title_en : newsItem.title_zh
                )}
              </Typography>

              <Box
                className="news-detail-description"
                dangerouslySetInnerHTML={{ __html: description }}
              />

              {/* ✅ Image gallery ตาม device และ viewport — แสดงใต้ descriptionEn / descriptionZh */}
              {(galleryDesktop.length || galleryTablet.length || galleryMobile.length) && (
                <Box sx={{
                  mt: { xs: 3, md: 5 }
                }}>

                  {/* Desktop gallery - แสดงเฉพาะบน desktop */}
                  {galleryDesktop.length > 0 && isDesktop && (
                    <Box sx={{
                      mb: 3
                    }}>
                      <Grid container spacing={2}>
                        {galleryDesktop.map((src, index) => (
                          <Grid key={src} size={{ xs: 12, sm: 6, md: 6 }}>
                            <Box
                              sx={{
                                cursor: "pointer",
                                overflow: "hidden",
                                borderRadius: 2,
                                border: "1px solid rgba(0,0,0,0.1)",
                                width: "100%",
                                height: "400px", // กำหนดความสูงคงที่
                              }}
                              onClick={() =>
                                handleOpenLightbox(galleryStartIndex + index)
                              }
                            >
                              <Image
                                src={src}
                                title={`${shareTitle} - desktop ${index + 1}`}
                                alt={`${shareTitle} - desktop ${index + 1}`}
                                width={600}
                                height={400}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover", // ครอบตัดภาพให้เต็มพื้นที่
                                }}
                                loading="lazy"
                                className="img-fluid"
                              />
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}

                  {/* Tablet gallery - แสดงเฉพาะบน tablet */}
                  {galleryTablet.length > 0 && isTablet && (
                    <Box sx={{
                      mb: 3
                    }}>
                      <Grid container spacing={2}>
                        {galleryTablet.map((src, index) => (
                          <Grid key={src} size={{ xs: 12, sm: 6, md: 6 }}>
                            <Box
                              sx={{
                                cursor: "pointer",
                                overflow: "hidden",
                                borderRadius: 2,
                                border: "1px solid rgba(0,0,0,0.1)",
                                width: "100%",
                                height: "400px", // กำหนดความสูงคงที่
                              }}
                              onClick={() =>
                                handleOpenLightbox(galleryStartIndex + index)
                              }
                            >
                              <Image
                                src={src}
                                title={`${shareTitle} - tablet ${index + 1}`}
                                alt={`${shareTitle} - tablet ${index + 1}`}
                                width={600}
                                height={400}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover", // ครอบตัดภาพให้เต็มพื้นที่
                                }}
                                loading="lazy"
                                className="img-fluid"
                              />
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}

                  {/* Mobile gallery - แสดงเฉพาะบน mobile */}
                  {galleryMobile.length > 0 && isMobile && (
                    <Box>
                      <Grid container spacing={2}>
                        {galleryMobile.map((src, index) => (
                          <Grid key={src} size={{ xs: 12, sm: 6, md: 6 }}>
                            <Box
                              sx={{
                                cursor: "pointer",
                                overflow: "hidden",
                                borderRadius: 2,
                                border: "1px solid rgba(0,0,0,0.1)",
                                width: "100%",
                                height: "300px", // ความสูงสำหรับ mobile
                              }}
                              onClick={() =>
                                handleOpenLightbox(galleryStartIndex + index)
                              }
                            >
                              <Image
                                src={src}
                                title={`${shareTitle} - mobile ${index + 1}`}
                                alt={`${shareTitle} - mobile ${index + 1}`}
                                width={400}
                                height={300}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover", // ครอบตัดภาพให้เต็มพื้นที่
                                }}
                                loading="lazy"
                                className="img-fluid"
                              />
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}
                  
                </Box>
              )}

            </Grid>

            {otherImages.map((imgSrc, index) => (
              <Grid key={index} size={{ xs: 12, md: 6 }} sx={{
                mt: { xs: 0, md: 5 }
              }}>
                <Box
                  sx={{ cursor: "pointer" }}
                  onClick={() => handleOpenLightbox(1 + index)}
                >
                  <Image
                    src={imgSrc}
                    width={1200}
                    height={650}
                    priority
                    title={`${
                      isEnglish ? newsItem.title_en : newsItem.title_zh
                    } - image ${index + 2}`}
                    alt={`${
                      isEnglish ? newsItem.title_en : newsItem.title_zh
                    } - image ${index + 2}`}
                    className="img-fluid"
                  />
                </Box>
              </Grid>
            ))}

            <Grid size={12} sx={{
              mt: { xs: 0, md: 2 }
            }}>
              <Divider />
              <Box sx={{
                mt: 3
              }}>
                <Typography variant="subtitle1">{t("share")}</Typography>
                <Box
                  sx={{ display: "flex", gap: 2, mt: 2 }}
                  className="social-icons"
                >
                  {socialShares.map((social) => (
                    <Link
                      key={social.name}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Image
                        src={social.icon}
                        title={social.name}
                        alt={social.name}
                        width={32}
                        height={32}
                      />
                    </Link>
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Container>
      {/* Related */}
      {relatedNewsLocal.length > 0 && (
        <Container sx={{ pb: { xs: 3, md: 10 }, pt: { xs: 3, md: 2 } }}>
          <Grid
            container
            sx={{
              display: "flex",
              alignItems: { xs: "start", md: "center" },
              mb: { xs: 3, md: 2 }
            }}>
            <Grid size={12}>
              <Typography component="h4" variant="h4">{t("related_contents")}</Typography>
            </Grid>
          </Grid>

          <Grid container spacing={3} id="news">
            {relatedNewsLocal.map((item) => {
              return (
                <Grid
                  key={item.id}
                  size={{ xs: 12, sm: 6, md: 4 }}
                  className="card-news"
                >
                  <Link
                    href={buildDetailUrl(
                      isEnglish ? item.slug_en : item.slug_zh
                    )}
                    style={{ display: "block", height: "100%" }}
                  >
                    <Grid container spacing={0} className="news">
                      <Grid
                        size={12}
                        className="news-image"
                        sx={getBackgroundImage({ src: item.img1 })}
                      />
                      <Grid size={12}>
                        <Box sx={{ p: 3 }}>
                          <Typography
                            variant="subtitle1"
                            title={isEnglish ? item.title_en : item.title_zh}
                            sx={{
                              mb: "1rem"
                            }}
                          >
                            {isEnglish ? item.title_en : item.title_zh}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid
                        container
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          p: 3,
                          pt: 0,
                          width: "100%",
                          mt: "auto"
                        }}>
                        <Grid size={12}>
                          <Divider />
                        </Grid>
                        <Grid size={6} className="date">
                          {item.date}
                        </Grid>
                      </Grid>
                    </Grid>
                  </Link>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      )}
      {/* Lightbox Modal */}
      <Modal
        open={lightboxOpen}
        onClose={handleCloseLightbox}
        closeAfterTransition
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <Fade in={lightboxOpen}>
          <Box
            sx={{
              position: "relative",
              maxWidth: "90vw",
              maxHeight: "90vh",
              outline: "none",
            }}
          >
            <IconButton
              onClick={handleCloseLightbox}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                color: "white",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.8)" },
                zIndex: 1,
              }}
            >
              <CloseIcon />
            </IconButton>

            {allImages.length > 1 && (
              <>
                <IconButton
                  onClick={handlePrevImage}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: 16,
                    transform: "translateY(-50%)",
                    color: "white",
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.8)" },
                  }}
                >
                  <ArrowBackIosNewIcon />
                </IconButton>
                <IconButton
                  onClick={handleNextImage}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    right: 16,
                    transform: "translateY(-50%)",
                    color: "white",
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.8)" },
                  }}
                >
                  <ArrowForwardIosIcon />
                </IconButton>
              </>
            )}
            <img
              src={allImages[selectedImageIndex]}
              alt={`News gallery image ${selectedImageIndex + 1}`}
              style={{
                maxWidth: "100%",
                maxHeight: "90vh",
                objectFit: "contain",
              }}
            />
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
}